import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@/lib/store';
import ThemeGraph, { deriveGraphPhase } from './ThemeGraph';

const MODE_A_LINES = [
  { cat: 'SYS',   text: 'Welcome to SIMNOPSIS' },
  { cat: 'STEP',  text: 'Upload audio, video, or paste a link' },
  { cat: 'STEP',  text: 'We accept MP3 · WAV · M4A · MP4 · MOV — up to 2 hours' },
  { cat: 'STEP',  text: 'Choose a highlight mode: Quick Cuts / Best Mix / Deep Cuts' },
  { cat: 'STEP',  text: 'Add a focus term to narrow results (optional)' },
  { cat: 'STEP',  text: 'Hit "Extract Highlights" to start' },
  { cat: 'SYS',   text: 'SIMNOPSIS scans the full recording' },
  { cat: 'THEME', theme: 'cyan', tag: 'THEMES', text: 'Themes are detected and grouped automatically' },
  { cat: 'HIGHLIGHT', text: 'Highlights are scored, ranked, and ready to play' },
  { cat: 'DONE',  text: 'Try it now — upload a file or run a sample below' },
];

const CAT_COLORS = {
  SYS: '#F97316',      // orange glass pill
  STEP: '#A78BFA',
  THEME: '#20B8F2',
  HIGHLIGHT: '#7EE06D',
  DONE: null, // gradient
  SCRIBE: '#F2B84C',
  EDIT: '#34D399',
};

const THEME_COLORS = { cyan: '#20B8F2', mint: '#7EE06D', violet: '#A78BFA', amber: '#F2B84C', coral: '#F2704C' };

export default function ProcessingConsole() {
  const { state } = useStore();
  const [modeALines, setModeALines] = useState([]);
  const [modeAIdx, setModeAIdx] = useState(0);
  const [livePct, setLivePct] = useState(0);
  const livePctRef = useRef(0);
  const progressTargetRef = useRef(0);
  const logsRef = useRef([]);

  useEffect(() => {
    progressTargetRef.current = Number(state.progressPct) || 0;
    logsRef.current = state.processingLogs || [];
  }, [state.progressPct, state.processingLogs]);

  // Mode A typewriter loop
  useEffect(() => {
    if (state.consoleMode !== 'idle') return;
    let cancelled = false;
    let holdT;

    const run = () => {
      if (cancelled) return;
      setModeALines([]);
      let i = 0;
      const step = () => {
        if (cancelled) return;
        if (i < MODE_A_LINES.length) {
          // Capture line before i++ — functional updaters run later and would otherwise read the next index
          const line = MODE_A_LINES[i];
          const idx = i;
          setModeALines(prev => [...prev, line]);
          setModeAIdx(idx);
          i++;
          holdT = setTimeout(step, 1000);
        } else {
          // Hold 22s then loop
          holdT = setTimeout(run, 22000);
        }
      };
      // Reduced motion: show all instantly, no loop
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setModeALines(MODE_A_LINES);
        return;
      }
      step();
    };
    run();
    return () => { cancelled = true; clearTimeout(holdT); };
  }, [state.consoleMode]);

  // Live % — follows store target smoothly; during transcribe never jumps to 100
  useEffect(() => {
    if (state.consoleMode !== 'processing') {
      if (state.consoleMode === 'complete') {
        livePctRef.current = 100;
        setLivePct(100);
      } else {
        livePctRef.current = 0;
        setLivePct(0);
      }
      return undefined;
    }

    // Fresh run — snap down if target reset
    if (progressTargetRef.current < livePctRef.current - 5) {
      livePctRef.current = progressTargetRef.current;
      setLivePct(progressTargetRef.current);
    }

    const phaseCeil = {
      uploading: 12,
      transcribing: 56, // hard cap while Simscribe works (~60–120s climb)
      detecting: 72,
      slicing: 88,
      titling: 96,
    };
    let raf;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min(0.08, (now - last) / 1000);
      last = now;
      const target = Math.max(0, Math.min(100, progressTargetRef.current));
      const phase = deriveGraphPhase('processing', logsRef.current);
      const ceil = phaseCeil[phase] ?? 95;

      // Chase store progress, but never above phase ceiling (blocks fake 100% while transcribing)
      let aim = target >= 100 ? 100 : Math.min(ceil, target);
      let cur = livePctRef.current;

      if (aim < cur - 1) {
        cur = aim; // reset on new run
      } else if (cur < aim) {
        const rate = phase === 'transcribing' ? 0.5
          : phase === 'uploading' ? 1.0
          : 2.2;
        cur = Math.min(aim, cur + rate * dt);
      }

      if (phase === 'transcribing') cur = Math.min(cur, phaseCeil.transcribing);
      if (phase === 'uploading') cur = Math.min(cur, phaseCeil.uploading);

      livePctRef.current = cur;
      setLivePct(cur);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state.consoleMode]);

  const isMB = state.consoleMode !== 'idle';
  const lines = isMB ? state.processingLogs : modeALines;
  const graphPhase = useMemo(
    () => deriveGraphPhase(state.consoleMode, isMB ? state.processingLogs : []),
    [state.consoleMode, state.processingLogs, isMB],
  );
  const displayPct = state.consoleMode === 'processing'
    ? Math.round(livePct)
    : (state.consoleMode === 'complete' ? 100 : 0);
  const processingSource = (state.processingLogs || []).find((l) => String(l.text || '').startsWith('Source ·'))?.text?.replace(/^Source ·\s*/, '');
  const processingRef = (state.processingLogs || []).find((l) => String(l.text || '').startsWith('Run started'))?.text?.replace(/^Run started —\s*/, '');
  const sourceLabel = state.currentRun?.source
    ? `${state.currentRun.runRef ? `${state.currentRun.runRef} · ` : ''}${state.currentRun.source} · ${state.currentRun.duration}`
    : (state.consoleMode === 'processing'
      ? [processingRef, processingSource].filter(Boolean).join(' · ') || 'Processing…'
      : 'Demo Preview');

  const statusInfo = () => {
    if (state.consoleMode === 'idle') return { label: 'STANDBY', color: 'rgba(255,255,255,0.3)', pulse: false, glow: 'none' };
    if (state.consoleMode === 'processing') return { label: 'PROCESSING', color: '#F2B84C', pulse: true, glow: '0 0 8px #F2B84C' };
    return { label: 'COMPLETE', color: '#7EE06D', pulse: false, glow: '0 0 8px #7EE06D' };
  };
  const s = statusInfo();

  return (
    <section id="console" data-testid="processing-console" className="py-8 sm:py-12">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="aurora-panel p-0 overflow-hidden reveal">
          {/* Header bar */}
          <div className="px-5 sm:px-6 py-3.5 border-b border-white/5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  background: s.color,
                  boxShadow: s.glow,
                  animation: s.pulse ? 'dot-pulse 1.2s ease-in-out infinite' : 'none',
                }}
              />
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">{s.label}</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
              {isMB ? sourceLabel : 'SIMNOPSIS · Demo Preview'}
            </div>
          </div>

          <div className="grid lg:grid-cols-[55%_45%]">
            {/* LEFT: log */}
            <div className="p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-white/5">
              <div
                className="font-mono text-[13px] leading-relaxed min-h-[300px] max-h-[420px] overflow-y-auto no-scrollbar"
                aria-live="polite"
                data-testid="console-log"
              >
                {lines.filter(Boolean).map((line, idx) => {
                  const barColor = line.tag === 'SCRIBE'
                    ? CAT_COLORS.SCRIBE
                    : line.tag === 'EDIT'
                      ? CAT_COLORS.EDIT
                    : line.cat === 'DONE'
                      ? null
                      : line.cat === 'THEME'
                        ? (line.theme ? THEME_COLORS[line.theme] : CAT_COLORS.THEME)
                        : CAT_COLORS[line.cat];
                  const tag = line.tag || line.cat;
                  return (
                    <div key={idx} className="log-line" style={{ animationDelay: `${idx * 60}ms` }}>
                      <span
                        className="bar"
                        style={{
                          background: barColor || 'linear-gradient(180deg, #20B8F2, #7EE06D)',
                        }}
                      />
                      <span
                        className="tag-pill"
                        style={{
                          background: line.cat === 'DONE'
                            ? 'linear-gradient(90deg, rgba(32,184,242,0.25), rgba(126,224,109,0.25))'
                            : line.cat === 'SYS'
                              ? 'rgba(249,115,22,0.16)'
                              : `${barColor}22`,
                          color: line.cat === 'DONE' ? '#7EE06D' : (barColor || '#F97316'),
                          border: `1px solid ${line.cat === 'DONE' ? 'rgba(126,224,109,0.35)' : line.cat === 'SYS' ? 'rgba(249,115,22,0.40)' : `${barColor}44`}`,
                          minWidth: 88,
                          justifyContent: 'center',
                          backdropFilter: 'blur(6px)',
                          WebkitBackdropFilter: 'blur(6px)',
                        }}
                      >
                        {line.cat === 'DONE' ? '✓ DONE' : tag}
                      </span>
                      <span className="text-white/80 truncate">{line.text}</span>
                      {isMB && line.t && (
                        <span className="text-white/35 tabular-nums">{line.t}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-white/5">
                {state.consoleMode === 'processing' || state.consoleMode === 'complete' ? (
                  <div data-testid="console-progress-block">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/45 truncate">
                        {state.consoleMode === 'complete'
                          ? 'Complete'
                          : graphPhase === 'uploading'
                            ? 'Uploading'
                            : graphPhase === 'transcribing'
                              ? 'Simscribe-X · Transcribing'
                              : graphPhase === 'detecting'
                                ? 'Detecting highlights'
                                : graphPhase === 'slicing'
                                  ? 'Slicing clips'
                                  : graphPhase === 'titling'
                                    ? 'summary + titles'
                                    : 'Processing'}
                      </span>
                      <span
                        className="font-display text-lg font-semibold tabular-nums leading-none shrink-0"
                        style={{
                          background: 'linear-gradient(90deg, #20B8F2, #7EE06D)',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                        data-testid="console-progress-pct"
                        aria-live="polite"
                      >
                        {displayPct}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden !border !border-white/12">
                      <div
                        className="h-full w-full origin-left rounded-full"
                        style={{
                          // scaleX keeps fill width in lockstep with % (gradient stays mapped to full track)
                          transform: `scaleX(${Math.max(0, Math.min(1, displayPct / 100))})`,
                          background: 'linear-gradient(90deg, #20B8F2, #52CBB0, #7EE06D)',
                          transition: 'transform 80ms linear',
                          boxShadow: '0 0 10px rgba(32,184,242,0.35)',
                        }}
                      />
                    </div>
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/35">
                      Themes map how highlights connect · Powered by SIMTECH AI
                    </div>
                  </div>
                ) : (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/35">
                    SIMNOPSIS · AI Audio-Highlight Discovery · Paste a link or drop a file above
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: graph */}
            <div className="p-5 sm:p-6 relative">
              <div className="flex items-center justify-between mb-2 gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {isMB
                    ? (graphPhase === 'transcribing'
                      ? 'Theme Graph · Listening'
                      : graphPhase === 'detecting'
                        ? 'Theme Graph · Mapping'
                        : graphPhase === 'slicing'
                          ? 'Theme Graph · Cutting'
                          : 'Theme Graph · Live Map')
                    : 'Theme Graph · Awaiting Input'}
                </span>
              </div>
              <ThemeGraph active={isMB} activeThemes={state.activeThemes} phase={graphPhase} />

              {state.consoleMode === 'complete' && state.currentRun && (
                <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center justify-center gap-2">
                  <span
                    className="tag-pill"
                    style={{
                      color: '#20B8F2',
                      background: 'rgba(32,184,242,0.12)',
                      border: '1px solid rgba(32,184,242,0.32)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    Duration: {state.currentRun.duration}
                  </span>
                  <span
                    className="tag-pill"
                    style={{
                      color: '#7EE06D',
                      background: 'rgba(126,224,109,0.12)',
                      border: '1px solid rgba(126,224,109,0.32)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    {state.currentRun.scanned} moments
                  </span>
                  <span
                    className="tag-pill"
                    style={{
                      color: '#A78BFA',
                      background: 'rgba(167,139,250,0.12)',
                      border: '1px solid rgba(167,139,250,0.32)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    {state.currentRun.themesFound} themes
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
